import { axiosInstance } from '../axiosInstans';

export interface FeatureConfig {
    enabled: boolean;
}

export interface SettingsResponse {
    features: Record<string, FeatureConfig>;
    configurations: {
        'force-double-signature': {
            withdrawal: boolean;
            deposit: boolean;
            inventory: boolean;
            disposal: boolean;
        };
    };
}

export class FeatureConfigService {
    private static instance: FeatureConfigService;
    private features: Map<string, boolean> = new Map();
    private configurations: Map<string, any> = new Map();
    private isLoadedState: boolean = false;
    private loadingPromise: Promise<void> | null = null;
    private subscribers: Set<() => void> = new Set();

    private constructor() {}

    public static getInstance(): FeatureConfigService {
        if (!FeatureConfigService.instance) {
            FeatureConfigService.instance = new FeatureConfigService();
        }
        return FeatureConfigService.instance;
    }

    public get isLoaded(): boolean {
        return this.isLoadedState;
    }

    public async loadFeatures(): Promise<void> {
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        this.loadingPromise = this.fetchFeatures();
        return this.loadingPromise;
    }

    private async fetchFeatures(): Promise<void> {
        try {
            const response = await axiosInstance.get('/settings/web');
            const data: SettingsResponse = response.data;

            // Clear existing data
            this.features.clear();
            this.configurations.clear();

            // Load features
            Object.entries(data.features).forEach(([key, config]) => {
                this.features.set(key, config.enabled);
            });

            // Load configurations
            Object.entries(data.configurations).forEach(([key, config]) => {
                this.configurations.set(key, config);
            });

            this.isLoadedState = true;
            this.notifySubscribers();
        } catch (error) {
            // Keep default values on error
            this.isLoadedState = true;
            this.notifySubscribers();
        }
    }

    public canAccess(featureKey: string): boolean {
        return this.features.get(featureKey) ?? false;
    }

    public subscribe(callback: () => void): () => void {
        this.subscribers.add(callback);
        return () => {
            this.subscribers.delete(callback);
        };
    }

    private notifySubscribers(): void {
        this.subscribers.forEach((callback) => callback());
    }

    public refreshFeatures(): Promise<void> {
        this.loadingPromise = null;
        this.isLoadedState = false;
        return this.loadFeatures();
    }

    // Method to manually set features (useful for testing or local development)
    public setFeature(key: string, enabled: boolean): void {
        this.features.set(key, enabled);
        this.notifySubscribers();
    }

    // Method to get configuration values
    public getConfiguration<T = any>(configKey: string): T | undefined {
        return this.configurations.get(configKey);
    }

    // Method to check if double signature is required for a transaction type
    public isDoubleSignatureRequired(
        transactionType: 'withdrawal' | 'deposit' | 'inventory' | 'disposal'
    ): boolean {
        const config = this.getConfiguration('force-double-signature');
        return config?.[transactionType] ?? false;
    }

    // Method to get all features (useful for debugging)
    public getAllFeatures(): Record<string, boolean> {
        return Object.fromEntries(this.features);
    }

    // Method to get all configurations (useful for debugging)
    public getAllConfigurations(): Record<string, any> {
        return Object.fromEntries(this.configurations);
    }

    // Method to clear features and reset state (useful for logout)
    public clearFeatures(): void {
        this.features.clear();
        this.configurations.clear();
        this.isLoadedState = false;
        this.loadingPromise = null;
        this.notifySubscribers();
    }
}

export default FeatureConfigService;
